import React, { useState, useEffect } from 'react';
import { Modal, Form, Select, Input, Button, Descriptions, Tag, Upload, message, Space, Divider } from 'antd';
import { UploadOutlined, FileTextOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { getById, updateEstatus, subirDocumento } from '../../../../apis/SolicitudDocumentoApi';
import moment from 'moment';

const { TextArea } = Input;
const { Option } = Select;

const EditSolicitudDocumento = ({ visible, solicitud, onClose }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [detalles, setDetalles] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (visible && solicitud) {
      cargarDetalle();
    }
  }, [visible, solicitud]);

  const cargarDetalle = async () => {
    try {
      const response = await getById(solicitud.id);
      if (response.ok) {
        setDetalles(response.payload);
        form.setFieldsValue({
          estatus: response.payload.estatus,
          respuesta_admin: response.payload.respuesta_admin || '',
        });
      }
    } catch (error) {
      console.error('Error al cargar detalle:', error);
      message.error('Error al cargar el detalle de la solicitud');
    }
  };

  const handleActualizarEstatus = async (values) => {
    setLoading(true);
    try {
      const payload = {
        estatus: values.estatus,
        respuesta_admin: values.respuesta_admin || null,
      };

      const response = await updateEstatus(solicitud.id, payload);
      if (response.ok) {
        message.success('Estatus actualizado exitosamente');
        onClose();
      } else {
        message.error('Error al actualizar el estatus');
      }
    } catch (error) {
      console.error('Error al actualizar estatus:', error);
      message.error('Error al actualizar el estatus');
    }
    setLoading(false);
  };

  const handleSubirDocumento = async () => {
    if (fileList.length === 0) {
      message.warning('Por favor selecciona un archivo');
      return;
    }

    const file = fileList[0];
    const reader = new FileReader();

    reader.onload = async (e) => {
      setUploading(true);
      try {
        const base64 = e.target.result.split(',')[1]; // Remover el prefijo data:...;base64,

        const payload = {
          solicitud_id: solicitud.id,
          file_base64: base64,
          filename: file.name,
        };

        const response = await subirDocumento(payload);
        if (response.ok) {
          message.success('Documento subido exitosamente. Solicitud aprobada.');
          setFileList([]);
          onClose();
        } else {
          message.error('Error al subir el documento');
        }
      } catch (error) {
        console.error('Error al subir documento:', error);
        message.error('Error al subir el documento');
      }
      setUploading(false);
    };

    reader.readAsDataURL(file);
  };

  const uploadProps = {
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
    beforeUpload: (file) => {
      // Validar tamaño del archivo (máximo 10MB)
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error('El archivo debe ser menor a 10MB');
        return false;
      }

      setFileList([file]);
      return false; // Prevenir el upload automático
    },
    fileList,
    maxCount: 1,
  };

  const getEstatusTag = (estatus) => {
    const config = {
      'Pendiente': { color: 'blue', icon: null },
      'En Proceso': { color: 'orange', icon: null },
      'Aprobado': { color: 'green', icon: <CheckCircleOutlined /> },
      'Rechazado': { color: 'red', icon: <CloseCircleOutlined /> },
    };
    const { color, icon } = config[estatus] || { color: 'default', icon: null };
    return <Tag color={color} icon={icon}>{estatus}</Tag>;
  };

  const getTipoDocumentoLabel = (tipo) => {
    const labels = {
      constancia_laboral: 'Constancia Laboral',
      carta_recomendacion: 'Carta de Recomendación',
      comprobante_ingresos: 'Comprobante de Ingresos',
      historial_nomina: 'Historial de Nómina',
      certificado_antiguedad: 'Certificado de Antigüedad',
      otro: 'Otro'
    };
    return labels[tipo] || tipo;
  };

  return (
    <Modal
      title={
        <Space>
          <FileTextOutlined />
          <span>Gestionar Solicitud de Documento #{solicitud?.id}</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={800}
      footer={null}
    >
      {detalles && (
        <>
          {/* Información de la solicitud */}
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Empleado" span={2}>
              <div>
                <div style={{ fontWeight: 500 }}>{detalles.empleado_nombre}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>{detalles.empleado_email}</div>
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="Tipo de Documento" span={2}>
              {getTipoDocumentoLabel(detalles.tipo_documento)}
            </Descriptions.Item>
            <Descriptions.Item label="Fecha de Solicitud">
              {moment(detalles.fecha_solicitud).format('DD/MM/YYYY HH:mm')}
            </Descriptions.Item>
            <Descriptions.Item label="Estatus">
              {getEstatusTag(detalles.estatus)}
            </Descriptions.Item>
            <Descriptions.Item label="Observaciones del Empleado" span={2}>
              {detalles.observaciones}
            </Descriptions.Item>
            {detalles.fecha_respuesta && (
              <Descriptions.Item label="Fecha de Respuesta">
                {moment(detalles.fecha_respuesta).format('DD/MM/YYYY HH:mm')}
              </Descriptions.Item>
            )}
            {detalles.respuesta_admin && (
              <Descriptions.Item label="Respuesta Admin" span={2}>
                {detalles.respuesta_admin}
              </Descriptions.Item>
            )}
            {detalles.archivo_nombre && (
              <Descriptions.Item label="Archivo Subido" span={2}>
                <Tag color="green" icon={<FileTextOutlined />}>
                  {detalles.archivo_nombre}
                </Tag>
              </Descriptions.Item>
            )}
          </Descriptions>

          <Divider />

          {/* Formulario de gestión */}
          <Form
            form={form}
            layout="vertical"
            onFinish={handleActualizarEstatus}
          >
            <Form.Item
              label="Cambiar Estatus"
              name="estatus"
              rules={[{ required: true, message: 'Selecciona un estatus' }]}
            >
              <Select size="large">
                <Option value="Pendiente">Pendiente</Option>
                <Option value="En Proceso">En Proceso</Option>
                <Option value="Aprobado">Aprobado</Option>
                <Option value="Rechazado">Rechazado</Option>
              </Select>
            </Form.Item>

            <Form.Item
              label="Respuesta / Comentarios"
              name="respuesta_admin"
            >
              <TextArea
                rows={4}
                placeholder="Escribe una respuesta o comentario para el empleado"
                showCount
                maxLength={500}
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                size="large"
              >
                Actualizar Estatus
              </Button>
            </Form.Item>
          </Form>

          <Divider>O subir documento directamente</Divider>

          {/* Sección de carga de archivo */}
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <Upload {...uploadProps}>
              <Button icon={<UploadOutlined />} size="large">
                Seleccionar Documento
              </Button>
            </Upload>
            {fileList.length > 0 && (
              <Button
                type="primary"
                onClick={handleSubirDocumento}
                loading={uploading}
                style={{ marginTop: 16 }}
                size="large"
                icon={<CheckCircleOutlined />}
              >
                Subir y Aprobar Solicitud
              </Button>
            )}
            <div style={{ marginTop: 12, color: '#999', fontSize: '12px' }}>
              Al subir el documento, la solicitud se marcará automáticamente como "Aprobado"
            </div>
          </div>
        </>
      )}
    </Modal>
  );
};

export default EditSolicitudDocumento;
